import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface RadioOption<T extends string> {
  value: T;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * Vertical list of exclusive choices with room for a line of explanation each.
 *
 * Use this over `SegmentedControl` when the options need justifying — a
 * segmented control has nowhere to put "compact is right for boards, exact for
 * finance", so that guidance ended up as separate helper text nobody connected
 * to the option it described.
 */
export function RadioGroup<T extends string>({
  value,
  onChange,
  options,
  name,
  ariaLabel,
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: RadioOption<T>[];
  name?: string;
  ariaLabel: string;
  className?: string;
}) {
  const autoName = useId();
  const groupName = name ?? autoName;

  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('space-y-1.5', className)}>
      {options.map((option) => {
        const id = `${groupName}-${option.value}`;
        const active = option.value === value;
        return (
          <label
            key={option.value}
            htmlFor={id}
            className={cn(
              'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 text-sm transition-colors duration-150',
              active ? 'border-primary/50 bg-primary/5' : 'border-border bg-surface hover:bg-muted/50',
              option.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <input
              id={id}
              type="radio"
              name={groupName}
              value={option.value}
              checked={active}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[hsl(var(--primary))] focus-ring"
            />
            <span className="min-w-0">
              <span className="block font-medium">{option.label}</span>
              {option.hint ? (
                <span className="mt-0.5 block text-[11px] text-muted-foreground">{option.hint}</span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}
